import { DocumentTypesTypes } from '@sunbit-dev/common/enums/DocumentTypes';
import IIdentificationRequest, { isIIdentificationRequest } from '@sunbit-dev/common/models/cardApply/IIdentificationRequest';
import IDocument from '@sunbit-dev/common/models/IDocument';
import WithOptional from '@sunbit-dev/common/typescript/WithOptional';
import React from 'react';
import styled, { css } from 'styled-components';
import { percentage } from '@sunbit-dev/common/helpers/numbers';
import scrollToTop from '../../../../helpers/scrollToTop';
import cardPicture from '../../../assets/card-apply/cardNewDesign.svg';
import { Dropdown } from '../../../ui/dropdown/Dropdown';
import HtmlViewer from '../../../ui/htmlViewer/HtmlViewer';
import Input2, { InputBackground } from '../../../ui/input/Input2';
import MoneyInput from '../../../ui/moneyInput/MoneyInput';
import CardApplyLink from '../../components/cardApplyLink/CardApplyLink';
import Tooltip from '../../../ui/tooltip/Tooltip';
import AgreeButton from '../../components/agreeButton/AgreeButton';
import CardSummary from '../../components/cardSummary/CardSummary';
import CommonSection from '../../components/commonSection/CommonSection';
import FullSsnRequired from '../../components/fullSsnRequired/FullSsnRequired';
import LegalText from '../../components/legalText/LegalText';
import OutOfRangeIncomeDialog from '../../components/outOfRangeIncomeDialog/outOfRangeIncomeDialog';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import Title from '../../components/title/Title';
import ShowPdfLink from '../../components/showPdfLink/ShowPdfLink';
import DatePicker from '../../../ui/datePickerNew/datePickerNew';
import InputTypes from '@sunbit-dev/common/enums/cardApply/InputTypes';
import LegaInfoNewAccount from '../../components/LegaInfoNewAccount/LegaInfoNewAccount';
import { PrivacyOptions } from '@sunbit-dev/common/enums/Datadog';

// ############################ Styles ############################
// tslint:disable:variable-name
const SIdentification = styled.div`
  display: flex;
  flex-direction: column;
`;

const SNotAffectScoreText = styled.p(
  ({ theme }) => `
  font-weight: ${theme.font.weight.bold};
  color: ${theme.colors.primary};
  font-size: ${theme.font.size.medium};
  @media only screen and ${theme.device.laptop} {
    font-size: ${theme.font.size.medium3};
  }
`
);

const SLayoutPartition = styled.section(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  @media only screen and ${theme.device.laptop} {
    flex-direction: row;
    & + & {
      margin-left: ${theme.spacing.large}px;
    }
  }
`
);

const SLayoutColumn = styled.section(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  flex 1;
  position: relative;
  @media only screen and ${theme.device.laptop} {
    & + & {
      margin-left: 20px;
    }
  }
`
);

const SCommonSection2 = styled(CommonSection)(
  ({ theme }) => `

  @media only screen and ${theme.device.laptop} {
    margin-bottom: ${theme.spacing.larger2}px;
  }
`
);

const SCommonSection3 = styled(SCommonSection2)(
  ({ theme }) => `
    display: flex;
    max-width: 460px;
    align-items: center;
    margin-bottom: ${theme.spacing.medium}px !important;
    > *:first-child { flex: 1; }
    > *:last-child { flex: 1.3; }
`
);

const SDropdown = styled(Dropdown)`
  max-width: 460px;
`;

const SMediumInput = styled(Input2)`
  max-width: 460px;
`;

const SMediumMoneyInput = styled(MoneyInput)`
  max-width: 460px;
`;

const SError = styled.section<{}>(
  ({ theme }) => `
  color: ${theme.colors.error};
`
);

const STermsAndConditionsBlock = styled.section(
  ({ theme }) => `
  border: 1px solid ${theme.colors.border};
  padding: 0;
  margin-bottom: 10px;
  max-height: 350px;
  overflow: auto;
  background-color: ${theme.colors.white};
`
);

const SCardImage = styled.img(
  ({ theme }) => `
  width: 100%;
  max-width: 250px;
  @media only screen and ${theme.device.laptop} {
    max-width: 518px;
    position: absolute;
    right: 0;
    transform: translateX(100%);
  }
`
);

const SCardSummary = styled(CardSummary)(
  ({ theme }) => `
  margin: 0 auto;
  @media only screen and ${theme.device.laptop} {
    position: absolute;
    top :-610px;
    left: calc(50% + 60px);
  }
`
);

const SDiablePopupDiv = styled.div(
  ({ theme }) => `
  font-size: ${theme.font.size.smallMedium};
  color: ${theme.colors.dark};
  margin-top: ${theme.spacing.small}px;
`
);

const SDatePicker = styled(DatePicker)(
  ({ theme }) => `
  max-width: 460px;
  label {
    font-size: ${theme.font.size.medium2};
  }
  input {
    background-color: white;
    text-align: center;
  }
  .inputInnerWrap {
    border-width: 0;
  }`
);

// tslint:enable:variable-name
// ############################ End Styles ###########################

export enum IdentificationErrors {
  detailsDontMatch = 'detailsDontMatch',
  fullSsnLoginRequired = 'fullSsnLoginRequired',
}

type IdentificationRequestValues = WithOptional<IIdentificationRequest, 'monthlyIncome' | 'monthlyHousing'>;

interface IIdentificationProps {
  firstName: string;
  applicationAprMin: number;
  applicationAprMax: number;
  onGetValidationCodeClick?: () => void;
  solicitationAgreement?: string;
  onFullSSNRequiredShow?: () => void;
  values: IdentificationRequestValues;
  submit: (values: IIdentificationRequest) => void;
  error?: IdentificationErrors | null;
  lowMonthlyIncomeLine: number;
  highMonthlyIncomeLine: number;
  referringPartner: string;
  onChangeMiddleware?: (onChange: (...props: any) => void, inputId: number) => (...props: any) => void;
}

// tslint:disable-next-line:variable-name
const Identification: React.FC<IIdentificationProps> = (props) => {
  const housingTypes = [
    { label: 'Own', value: 'own' },
    { label: 'Live with family', value: 'family' },
    { label: 'Rent', value: 'rent' },
  ];

  const MAX_OUT_OF_RANGE_INCOME_TRYES = 1;

  const [values, setValues] = React.useState<IdentificationRequestValues>({
    monthlyIncome: undefined,
    ssnLast4Digits: '',
    monthlyHousing: undefined,
    housingExpenseType: housingTypes[0].value,
  });
  const [OutOfRangeIncomeTrys, setOutOfRangeIncomeTrys] = React.useState<number>(0);
  const [OutOfRangeIncomeDialogOpen, setOutOfRangeIncomeDialogOpen] = React.useState<boolean>(false);
  const [isDateValid, setIsDateValid] = React.useState<boolean>(false);
  const monthlyIncomeInputRef = React.useRef<HTMLInputElement>(null);

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);

  React.useEffect(() => {
    const newInputs: Partial<IIdentificationProps['values']> = {};
    if (values.monthlyIncome !== props.values.monthlyIncome) {
      newInputs.monthlyIncome = props.values.monthlyIncome || undefined;
    }
    if (values.housingExpenseType !== props.values.housingExpenseType) {
      newInputs.housingExpenseType = props.values.housingExpenseType || 'own';
    }
    if (values.monthlyHousing !== props.values.monthlyHousing) {
      newInputs.monthlyHousing = props.values.monthlyHousing || undefined;
    }
    if (values.ssnLast4Digits !== props.values.ssnLast4Digits) {
      newInputs.ssnLast4Digits = props.values.ssnLast4Digits || '';
    }

    if (Object.keys(newInputs).length) {
      setValues({ ...values, ...newInputs });
    }
  }, []);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setValues({ ...values, [name]: value });
    },
    [values]
  );

  const handleMoneyInputChange = React.useCallback(
    (value: string, name: string) => {
      const valueToSet = value === '' ? undefined : Number(value);
      setValues({ ...values, [name]: valueToSet });
    },
    [values]
  );

  const handleHousingTypeChanged = React.useCallback(
    (value: string) => {
      setValues({ ...values, housingExpenseType: value });
    },
    [values]
  );

  const handleDateChange = (value: string, name: 'dateOfBirth') => {
    setValues({ ...values, [name]: value });
  };

  const submitWithFullSsn = React.useCallback(
    (ssn) => {
      if (isIIdentificationRequest(values)) {
        const { ssnLast4Digits, ...rest } = values;
        props.submit({ ...rest, fullSsn: ssn });
      }
    },
    [values]
  );

  const isSubmitEnabled = () => {
    if (isIIdentificationRequest(values)) {
      return values.housingExpenseType.length && values.ssnLast4Digits && ((props.referringPartner !== '' && isDateValid) || props.referringPartner === '') && values.ssnLast4Digits.length === 4;
    }
    return false;
  };

  const handleCloseOuOfRangeDialog = () => {
    setOutOfRangeIncomeDialogOpen(false);
    setOutOfRangeIncomeTrys(OutOfRangeIncomeTrys + 1);
  };

  const handleSubmit = React.useCallback(
    (_event: any, force: boolean = false) => {
      const salaryOutOfRange = Number(values.monthlyIncome) < props.lowMonthlyIncomeLine || Number(values.monthlyIncome) > props.highMonthlyIncomeLine;
      if (isSubmitEnabled()) {
        if (!force && OutOfRangeIncomeTrys < MAX_OUT_OF_RANGE_INCOME_TRYES && salaryOutOfRange) {
          setOutOfRangeIncomeDialogOpen(true);
        } else {
          scrollToTop();
          if (isIIdentificationRequest(values)) {
            props.submit({ ...values });
          }
        }
      }
    },
    [values, OutOfRangeIncomeTrys]
  );

  const errorText = props.error === IdentificationErrors.detailsDontMatch ? 'The information provided does not match our records. Please try again.' : '';
  return (
    <SIdentification data-dd-privacy={PrivacyOptions.MaskUserInput}>
      {props.error === IdentificationErrors.fullSsnLoginRequired && <FullSsnRequired onShow={props.onFullSSNRequiredShow} submit={submitWithFullSsn} />}
      <SLayoutPartition>
        <SLayoutColumn>
          <SCommonSection2>
            <Title data-at="sunbit-card-title">Apply now for your Sunbit Card!</Title>
          </SCommonSection2>
          <SCommonSection2>
            <SectionTitle data-at="info-intro">
              {props.referringPartner === '' && (
                <span data-at="firstName" data-dd-privacy={PrivacyOptions.Mask}>
                  {props.firstName}
                  ,
                  <br />
                  <br />
                </span>
              )}
              We just need a few pieces of information to complete your application.
            </SectionTitle>
          </SCommonSection2>
          <SCardImage src={cardPicture} alt="card picture" />
          <CommonSection>
            <SError data-at="error-message">{errorText}</SError>
          </CommonSection>
          <SCommonSection2 data-at="last4DigitsSSNInputGroup">
            <SectionTitle data-at="last4DigitsSSNTitle">
              Last 4 digits of your SSN{' '}
              <Tooltip
                dataAt="ssn-digits-tooltip"
                width={200}
                toggler={
                  <i className="material-icons" data-at="ssn-digits-tooltip-toggle">
                    info
                  </i>
                }
              >
                We use the last 4 digits of your Social Security Number to verify your identity.
              </Tooltip>
            </SectionTitle>
            <SMediumInput
              color={'primary'}
              placeholder="e.g. 1234"
              name="ssnLast4Digits"
              dataAt="last4DigitsSSN"
              value={values.ssnLast4Digits}
              onChange={props.onChangeMiddleware ? props.onChangeMiddleware(handleInputChange, InputTypes.SSN) : handleInputChange}
              pattern="^\d{0,4}$"
              centeredText
              background={InputBackground.White}
            />
          </SCommonSection2>

          {props.referringPartner !== '' && (
            <SCommonSection2 data-at="dateOfBirthSection">
              <SDatePicker
                name="dateOfBirth"
                className="inputInGroup"
                label="Date of Birth"
                onChange={handleDateChange}
                value={props.values.dateOfBirth || ''}
                maxDate={maxDate}
                placeholder="mm/dd/yyyy"
                isDateValid={isDateValid}
                setIsDateValid={setIsDateValid}
              />
            </SCommonSection2>
          )}
          <SCommonSection2 data-at="incomeInputGroup">
            <SectionTitle data-at="incomeTitle">
              Total net household monthly income{' '}
              <Tooltip
                dataAt="income-tooltip"
                width={200}
                toggler={
                  <i data-at="income-tooltip-toggle" className="material-icons">
                    info
                  </i>
                }
              >
                This is the amount you take home after taxes or the amount your household can reasonably expect to have access to each month. Income includes current or reasonably expected wages,
                salary, retirement income, public assistance, alimony, child support, interest or dividends, and separate maintenance income. You don’t have to reveal alimony, child support, or
                separate maintenance income if you don’t want to have it considered as a basis for repaying this obligation. If you are under age 21, include only your own income.
              </Tooltip>
            </SectionTitle>

            <SMediumMoneyInput
              color={'primary'}
              placeholder="e.g. $6,500"
              name="monthlyIncome"
              dataAt="income"
              value={String(values.monthlyIncome || '')}
              onChange={props.onChangeMiddleware ? props.onChangeMiddleware(handleMoneyInputChange, InputTypes.INCOME) : handleMoneyInputChange}
              centeredText
              background={InputBackground.White}
              ref={monthlyIncomeInputRef}
            />

            {OutOfRangeIncomeDialogOpen && (
              <OutOfRangeIncomeDialog
                onCloseAction={handleCloseOuOfRangeDialog}
                actionList={[
                  {
                    label: 'Update my income',
                    onClick: () => {
                      handleCloseOuOfRangeDialog();
                      if (monthlyIncomeInputRef.current) {
                        monthlyIncomeInputRef.current.focus();
                        monthlyIncomeInputRef.current.select();
                      }
                    },
                  },
                  {
                    label: 'Continue',
                    onClick: () => {
                      setOutOfRangeIncomeDialogOpen(false);
                      handleSubmit(null, true);
                    },
                  },
                ]}
              />
            )}
          </SCommonSection2>

          <SCommonSection2>
            <br />
            <SCommonSection2 data-dd-privacy={PrivacyOptions.Mask}>
              <SectionTitle data-at={'housingExpensesTitle'} data-dd-privacy={PrivacyOptions.Allow}>
                Housing status
              </SectionTitle>
              <SDropdown
                inputColor="primary"
                name="housingExpenseType"
                dataAt="housingExpenseType-input"
                onChange={handleHousingTypeChanged}
                value={values.housingExpenseType}
                dropdownOptions={housingTypes}
              />
            </SCommonSection2>

            <SCommonSection2>
              <SectionTitle data-at={'housingExpensesTitle'}>Housing expenses</SectionTitle>
              <SMediumMoneyInput
                color={'primary'}
                placeholder="e.g. $1,200"
                name="monthlyHousing"
                dataAt="monthlyHousing-input"
                value={String(values.monthlyHousing || '')}
                onChange={handleMoneyInputChange}
                centeredText
              />
            </SCommonSection2>
          </SCommonSection2>
        </SLayoutColumn>
        <SLayoutColumn />
      </SLayoutPartition>
      <SLayoutColumn>
        <SCommonSection2>
          <SNotAffectScoreText data-at="notAffectScoreText">Checking if you’re preliminarily approved will not affect your credit score*</SNotAffectScoreText>
        </SCommonSection2>
        <CommonSection>
          <SectionTitle data-at="termsAndConditions">Terms and Conditions</SectionTitle>
          <STermsAndConditionsBlock>
            <HtmlViewer content={props.solicitationAgreement} />
          </STermsAndConditionsBlock>
        </CommonSection>
        <LegaInfoNewAccount />
        <SCommonSection2>
          <AgreeButton dataAt="verify" disabled={!isSubmitEnabled()} onClick={handleSubmit}>
            Agree & Continue
          </AgreeButton>
        </SCommonSection2>
        <CommonSection>
          <LegalText dataAt="importantAboutNewAccount">
            IMPORTANT INFORMATION ABOUT OPENING A NEW ACCOUNT
            <br />
            To help the government fight the funding of terrorism and money laundering activities, federal law requires all financial institutions to obtain, verify, and record information that
            identifies each person who opens an account. What this means for you: When you open an account, we will ask for information that will allow us to identify you.
            <br />
            <br />
            *To check if you are preliminarily approved, only a soft credit check, which does not affect your credit, will be performed. If you complete the application and open a card account, the
            account opening (and all payment activity) will be reported to a major credit bureau, which may affect your credit.
          </LegalText>
        </CommonSection>

        <CommonSection>
          <SCardSummary />
        </CommonSection>

        <CommonSection>
          <LegalText dataAt="subjectToApproval">
            +The Sunbit Card is subject to approval based on creditworthiness and state of residence. {percentage(props.applicationAprMin)}-{percentage(props.applicationAprMax)} variable APR. Rates as
            of{' '}
            {new Date().toLocaleDateString('en-US', {
              timeZone: 'America/Los_Angeles',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })}
            . Residents of the following states are subject to the specified maximum APRs: MA: 22.99%. Not available to residents of CO, DC, MD, VT, WV or the US territories. The Sunbit Card is issued
            by Transportation Alliance Bank, Inc., dba TAB Bank, pursuant to a license from Visa U.S.A. Inc. Use of the card is subject to the cardholder agreement. Terms are subject to change, see
            our{' '}
            <CardApplyLink data-at="fax-Link" href="https://sunbit.com/im-a-consumer/sunbit-card-faq/" target="_blank">
              FAQs{' '}
            </CardApplyLink>
            to learn more about how the Sunbit Card works.
            <br />
            <br />
            ^Available on select devices.
          </LegalText>
        </CommonSection>
      </SLayoutColumn>
    </SIdentification>
  );
};

export default Identification;
